import { useEffect, useMemo } from 'react';
import { Alert, Box, Card, CardContent, Chip, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import CoreReportsService from '../../../services/CoreReportsService';
import { ChartCard, ColumnChart, SERIES_COLORS, StatTile } from './ReportCharts';
import { compact, count, formatDateTime, periodLabel } from './reportsFormat';
import { errorRateText, formatDuration, jobNeedsAttention, jobStateInfo, reconcileCases } from './opsText';
import useReport from './useReport';
import ReportState from './ReportState';

export default function HealthTab({ days, onFreshness }) {
  const params = useMemo(() => ({ days }), [days]);
  const report = useReport((p) => CoreReportsService.health(p), params);
  const data = report.data;
  const generatedAt = data?.generatedAt;
  useEffect(() => { onFreshness?.(generatedAt ? { live: true, generatedAt } : null); }, [generatedAt, onFreshness]);

  return <ReportState report={report}>{data && <Content data={data} />}</ReportState>;
}

function Content({ data }) {
  const { jobs, api, errorSeries, topErrorSources } = data;
  const attention = jobs.filter(jobNeedsAttention).length;
  const systemErrors = errorSeries.reduce((a, d) => a + d.errors, 0);
  const reconcile = jobs.find((j) => j.key === 'core_billing_reconcile');
  const cases = reconcileCases(reconcile?.details);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Jobs que necesitan atención" value={count(attention)} hint={attention === 0 ? 'Todos corren como se espera' : 'Fallaron, terminaron con fallos o están atrasados'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Errores 5xx de la API" value={api.tracked ? count(api.errors5xx) : '-'} hint={api.tracked ? `${errorRateText(api.errors5xx, api.requests)} de ${compact(api.requests)} pedidos` : 'Sin medir todavía'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Latencia promedio" value={api.tracked ? formatDuration(api.avgMs) : '-'} hint="De todos los endpoints" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile label="Errores del sistema" value={count(systemErrors)} hint="Arranque, tareas internas y excepciones no controladas" />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Jobs programados</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Resultado de la última corrida de cada job</Typography>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Job</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Última corrida</TableCell>
                  <TableCell align="right">Duración</TableCell>
                  <TableCell align="right">Bien</TableCell>
                  <TableCell align="right">Fallos</TableCell>
                  <TableCell align="right">Pendientes</TableCell>
                  <TableCell>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => {
                  const info = jobStateInfo(job);
                  return (
                    <TableRow key={job.key} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.label}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{job.schedule}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={info.color} variant={job.state === 'OK' && !job.late ? 'filled' : 'outlined'} label={info.label} />
                        {job.consecutiveFailures > 1 && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>{job.consecutiveFailures} fallas seguidas</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {job.lastFinishedAt ? formatDateTime(job.lastFinishedAt) : 'Nunca corrió'}
                        {job.state === 'FAILED' && job.lastSuccessAt && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Último éxito: {formatDateTime(job.lastSuccessAt)}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{job.lastDurationMs === null || job.lastDurationMs === undefined ? '-' : formatDuration(job.lastDurationMs)}</TableCell>
                      <TableCell align="right">{job.state === 'NEVER' ? '-' : count(job.processed)}</TableCell>
                      <TableCell align="right">{job.state === 'NEVER' ? '-' : count(job.failed)}</TableCell>
                      <TableCell align="right">{job.pending === null || job.pending === undefined ? '-' : count(job.pending)}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Tooltip title={job.message || ''}><Typography variant="body2" noWrap>{job.message || '-'}</Typography></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {cases && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Conciliación de Mercado Pago: casos que no se resolvieron solos</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Última corrida: {count(cases.checked)} cuenta(s) revisada(s)</Typography>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile label="Referencias ambiguas" value={count(cases.ambiguous)} hint="Suscripciones sin asignar: hoy no hay forma de vincularlas a mano" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile label="Límite de pedidos (429)" value={count(cases.rateLimited)} hint="Mercado Pago frenó los pedidos y los reintentos no alcanzaron" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile label="Cuentas con error" value={count(cases.accountErrors)} hint={`${count(cases.sweepErrors)} error(es) en el barrido general`} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatTile label="Reemplazadas (esperado)" value={count(cases.superseded)} hint="Quedan de cancelar y volver a suscribirse: no hay nada que hacer" />
              </Grid>
            </Grid>
            {cases.ambiguous > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Hay {count(cases.ambiguous)} suscripción(es) de Mercado Pago que no se pueden asignar a una cuenta de cobro. La solución hoy es identificar al pagador en el panel de Mercado Pago; el sistema todavía no tiene un camino manual para vincularlas.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>API</Typography>
      {!api.tracked ? (
        <Alert severity="info">Todavía no hay datos de tráfico: el contador de pedidos empieza a medir cuando se despliega esta versión del backend.</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Pedidos por día" subtitle="Todos los endpoints, todas las instancias"
                table={{ columns: ['Día', 'Pedidos'], rows: api.series.map((d) => [periodLabel(d.date, 'day'), count(d.requests)]) }}
              >
                <ColumnChart data={api.series.map((d) => ({ label: periodLabel(d.date, 'day'), value: d.requests }))} seriesName="Pedidos" />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Errores 5xx por día" subtitle="Escala propia: no se mezcla con los pedidos"
                table={{ columns: ['Día', 'Errores 5xx'], rows: api.series.map((d) => [periodLabel(d.date, 'day'), count(d.errors5xx)]) }}
              >
                <ColumnChart
                  data={api.series.map((d) => ({ label: periodLabel(d.date, 'day'), value: d.errors5xx }))} seriesName="Errores 5xx"
                  color={SERIES_COLORS[1]} format={(v) => String(Math.round(v))}
                />
              </ChartCard>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}><RoutesCard title="Endpoints con más errores 5xx" routes={api.topErrorRoutes} empty="Ningún endpoint devolvió errores 5xx en el período." /></Grid>
            <Grid item xs={12}>
              <RoutesCard title="Endpoints más lentos" subtitle="Por p95 aproximado, con al menos 20 pedidos" routes={api.slowestRoutes} empty="Todavía no hay endpoints con suficientes pedidos." />
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Errores del sistema por día" subtitle="Registrados en el core (no incluye errores de pedidos de una empresa)"
            table={{ columns: ['Día', 'Errores', 'Avisos'], rows: errorSeries.map((d) => [periodLabel(d.date, 'day'), count(d.errors), count(d.warnings)]) }}
          >
            <ColumnChart data={errorSeries.map((d) => ({ label: periodLabel(d.date, 'day'), value: d.errors }))} seriesName="Errores" color={SERIES_COLORS[1]} format={(v) => String(Math.round(v))} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Orígenes con más errores</Typography>
              {topErrorSources.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>No hubo errores del sistema en el período.</Typography>
              ) : (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead><TableRow><TableCell>Origen</TableCell><TableCell>Tipo</TableCell><TableCell align="right">Errores</TableCell><TableCell align="right">Último</TableCell></TableRow></TableHead>
                  <TableBody>
                    {topErrorSources.map((s) => (
                      <TableRow key={s.source}>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.source}</TableCell>
                        <TableCell>{s.category}</TableCell>
                        <TableCell align="right">{count(s.errors)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.lastAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function RoutesCard({ title, subtitle, routes, empty }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>}
        {routes.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{empty}</Typography>
        ) : (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Endpoint</TableCell><TableCell align="right">Pedidos</TableCell><TableCell align="right">5xx</TableCell>
                <TableCell align="right">Promedio</TableCell><TableCell align="right">p95</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {routes.map((r) => (
                <TableRow key={`${r.method} ${r.route}`}>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}><b>{r.method}</b> {r.route}</TableCell>
                  <TableCell align="right">{count(r.requests)}</TableCell>
                  <TableCell align="right">{count(r.errors5xx)} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>({errorRateText(r.errors5xx, r.requests)})</Typography></TableCell>
                  <TableCell align="right">{formatDuration(r.avgMs)}</TableCell>
                  <TableCell align="right">{formatDuration(r.p95Ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
